import { Add, Delete, Edit, CheckCircle, Cancel } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  EmissionFactorService,
  type EmissionFactor,
  type CreateEmissionFactorRequest,
  type UpdateEmissionFactorRequest,
} from '../../services/emissionFactorService';
import { ToastService } from '../../services/toastService';
import ConfirmationPopover from '../../components/ConfirmationPopover/ConfirmationPopover';
import { useConfirmationPopover } from '../../hooks/useConfirmationPopover';

interface EmissionFactorFormData {
  fuelType: string;
  emissionFactor: number;
  isActive: boolean;
}

export const EmissionFactorsPage = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingFactor, setEditingFactor] = useState<EmissionFactor | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [factorToDelete, setFactorToDelete] = useState<EmissionFactor | null>(null);
  const queryClient = useQueryClient();

  // Confirmation popover for delete action
  const { anchorEl, isOpen, openConfirmation, closeConfirmation } = useConfirmationPopover();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EmissionFactorFormData>({
    defaultValues: {
      fuelType: '',
      emissionFactor: 3.0,
      isActive: true,
    },
  });

  const {
    register: editRegister,
    handleSubmit: editHandleSubmit,
    reset: editReset,
    control: editControl,
    formState: { errors: editErrors },
  } = useForm<EmissionFactorFormData>({
    defaultValues: {
      fuelType: '',
      emissionFactor: 3.0,
      isActive: true,
    },
  });

  // Fetch emission factors
  const {
    data: emissionFactors = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['emission-factors'],
    queryFn: EmissionFactorService.getAll,
    select: (data) => (data ? [...data].sort((a, b) => a.fuelType.localeCompare(b.fuelType)) : []),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateEmissionFactorRequest) => EmissionFactorService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emission-factors'] });
      setIsCreateDialogOpen(false);
      reset();
      ToastService.success('Emission factor created successfully');
    },
    onError: (error) => {
      console.error('Failed to create emission factor:', error);
      ToastService.error('Failed to create emission factor');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEmissionFactorRequest }) =>
      EmissionFactorService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emission-factors'] });
      setIsEditDialogOpen(false);
      setEditingFactor(null);
      editReset();
      ToastService.success('Emission factor updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update emission factor:', error);
      ToastService.error('Failed to update emission factor');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => EmissionFactorService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emission-factors'] });
      closeConfirmation();
      setFactorToDelete(null);
      ToastService.success('Emission factor deleted successfully');
    },
    onError: (error) => {
      console.error('Failed to delete emission factor:', error);
      ToastService.error('Failed to delete emission factor');
    },
  });

  const handleOpenCreateDialog = () => {
    reset({
      fuelType: '',
      emissionFactor: 3.0,
      isActive: true,
    });
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    reset();
  };

  const handleCreate = (data: EmissionFactorFormData) => {
    createMutation.mutate({
      fuelType: data.fuelType,
      emissionFactor: Number(data.emissionFactor),
      isActive: data.isActive,
    });
  };

  const handleOpenEditDialog = (factor: EmissionFactor) => {
    setEditingFactor(factor);
    editReset({
      fuelType: factor.fuelType,
      emissionFactor: factor.emissionFactor,
      isActive: factor.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingFactor(null);
    editReset();
  };

  const handleEdit = (data: EmissionFactorFormData) => {
    if (!editingFactor) return;
    updateMutation.mutate({
      id: editingFactor.id,
      data: {
        fuelType: data.fuelType,
        emissionFactor: Number(data.emissionFactor),
        isActive: data.isActive,
      },
    });
  };

  const handleDeleteClick = (
    factor: EmissionFactor,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setFactorToDelete(factor);
    openConfirmation(event.currentTarget);
  };

  const handleConfirmDelete = () => {
    if (factorToDelete) {
      deleteMutation.mutate(factorToDelete.id);
    }
  };

  const handleToggleActive = (factor: EmissionFactor) => {
    updateMutation.mutate({
      id: factor.id,
      data: { isActive: !factor.isActive },
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load emission factors. Please try again later.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Emission Factors
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage fuel type emission factors for off-hire EUA calculations (EU ETS standard values)
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreateDialog}>
          Add Emission Factor
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Off-Hire Calculation Formula:</strong> (Fuel Consumption MT × Emission Factor) ×
          Voyage Type Percentage (100% for Intra-EU, 50% for Into/Out of EU) = EUA Deduction
        </Typography>
      </Alert>

      {/* Emission Factors Table */}
      <TableContainer component={Paper} sx={{ flex: 1 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Fuel Type</TableCell>
              <TableCell align="right">Emission Factor</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emissionFactors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No emission factors configured. Click "Add Emission Factor" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              emissionFactors.map((factor) => (
                <TableRow key={factor.id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight={500}>
                      {factor.fuelType}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" fontFamily="monospace">
                      {factor.emissionFactor.toFixed(3)} tCO₂/t fuel
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={factor.isActive ? <CheckCircle /> : <Cancel />}
                      label={factor.isActive ? 'Active' : 'Inactive'}
                      color={factor.isActive ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(factor)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditDialog(factor)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(factor, e)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleCreate)}>
          <DialogTitle>Add Emission Factor</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Fuel Type"
                {...register('fuelType', { required: 'Fuel type is required' })}
                error={!!errors.fuelType}
                helperText={errors.fuelType?.message || 'e.g., HFO, LFO, MDO/MGO, LNG'}
                fullWidth
              />
              <TextField
                label="Emission Factor (tCO₂/t fuel)"
                type="number"
                inputProps={{ step: '0.001', min: '0.001' }}
                {...register('emissionFactor', {
                  required: 'Emission factor is required',
                  validate: (value) =>
                    (typeof value === 'number' ? value : parseFloat(value)) > 0 ||
                    'Factor must be positive',
                })}
                error={!!errors.emissionFactor}
                helperText={
                  errors.emissionFactor?.message || 'tCO₂ emitted per tonne of fuel burned'
                }
                fullWidth
              />
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label="Active"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <form onSubmit={editHandleSubmit(handleEdit)}>
          <DialogTitle>Edit Emission Factor</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Fuel Type"
                {...editRegister('fuelType', { required: 'Fuel type is required' })}
                error={!!editErrors.fuelType}
                helperText={editErrors.fuelType?.message}
                fullWidth
              />
              <TextField
                label="Emission Factor (tCO₂/t fuel)"
                type="number"
                inputProps={{ step: '0.001', min: '0.001' }}
                {...editRegister('emissionFactor', {
                  required: 'Emission factor is required',
                  min: { value: 0.001, message: 'Factor must be positive' },
                })}
                error={!!editErrors.emissionFactor}
                helperText={editErrors.emissionFactor?.message}
                fullWidth
              />
              <Controller
                name="isActive"
                control={editControl}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label="Active"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Popover */}
      <ConfirmationPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeConfirmation}
        onConfirm={handleConfirmDelete}
        title="Delete Emission Factor"
        message={`Are you sure you want to delete "${factorToDelete?.fuelType}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteMutation.isPending}
      />
    </Box>
  );
};

export default EmissionFactorsPage;
